import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import Breadcrumb from '@/components/common/Breadcrumb';
import Input from '@/components/forms/Input';
import Select from '@/components/forms/Select';
import Button from '@/components/ui/Button';
import Notification from '@/components/ui/Notification';
import { Icon } from '@iconify/react';
import ProductApiService, { Product } from '@/services/productApiService';
import PaymentMethodApiService, { PaymentMethod } from '@/services/paymentMethodApiService';
import { productOrderService, ProductOrderPayload } from '@/services/productOrderService';
import { requireArenaSlug } from '@/utils/arena';
import { getAdminBasePath } from '@/utils/adminPaths';
import { openExternalUrl } from '@/utils/openExternalUrl';

interface ProductSellFormValues {
  customerName: string;
  phoneNumber: string;
  transactionDate: string;
  paymentMethod: string;
}

interface SelectedProduct extends Product {
  quantity: number;
}

const initialValues: ProductSellFormValues = {
  customerName: '',
  phoneNumber: '',
  transactionDate: new Date().toISOString().split('T')[0], // Today's date
  paymentMethod: '',
};

const validationSchema = Yup.object({
  customerName: Yup.string().required('Customer name wajib diisi'),
  phoneNumber: Yup.string()
    .matches(/^[0-9+\-\s()]+$/, 'Format nomor telepon tidak valid'),
  transactionDate: Yup.string().required('Transaction date wajib diisi'),
  paymentMethod: Yup.string().required('Payment method wajib diisi'),
});

const ProductSell = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);
  const [notification, setNotification] = useState({
    isOpen: false,
    message: '',
    variant: 'success' as 'success' | 'error',
  });

  // Load products on component mount
  useEffect(() => {
    loadProducts();
    loadPaymentMethods();
  }, []);

  // Search products when search query changes
  useEffect(() => {
    if (searchQuery.trim()) {
      searchProducts();
    } else {
      loadProducts();
    }
  }, [searchQuery]);

  const loadPaymentMethods = async () => {
    try {
      setIsLoadingPaymentMethods(true);
      const methods = await PaymentMethodApiService.getPaymentMethods();
      // Filter hanya Cash payment method
      const cashMethods = methods.filter(
        (method) => method.code === 'Cash' || method.code === 'QRIS'
      );
      setPaymentMethods(cashMethods);
    } catch (error: any) {
      console.error('Error loading payment methods:', error);
      setNotification({
        isOpen: true,
        message: 'Gagal memuat metode pembayaran',
        variant: 'error',
      });
    } finally {
      setIsLoadingPaymentMethods(false);
    }
  };

  const loadProducts = async () => {
    try {
      setIsLoadingProducts(true);
      const productData = await ProductApiService.getProductsByArena(true);
      console.log('Loaded products:', productData);
      // Ensure productData is an array
      setProducts(Array.isArray(productData) ? productData : []);
    } catch (error: any) {
      console.error('Failed to load products:', error);
      setProducts([]); // Set empty array on error
      setNotification({
        isOpen: true,
        message: error.message || 'Gagal memuat produk',
        variant: 'error',
      });
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const searchProducts = async () => {
    try {
      setIsLoadingProducts(true);
      const productData = await ProductApiService.searchProducts(searchQuery);
      // Ensure productData is an array
      setProducts(Array.isArray(productData) ? productData : []);
    } catch (error: any) {
      console.error('Failed to search products:', error);
      setProducts([]); // Set empty array on error
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const addProduct = (product: Product) => {
    const existingProduct = selectedProducts.find((p) => p.id === product.id);

    if (existingProduct) {
      // Increase quantity if product already selected
      setSelectedProducts((prev) =>
        prev.map((p) => (p.id === product.id ? { ...p, quantity: p.quantity + 1 } : p))
      );
    } else {
      // Add new product with quantity 1
      setSelectedProducts((prev) => [...prev, { ...product, quantity: 1 }]);
    }
  };

  const updateProductQuantity = (productId: number, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeProduct(productId);
    } else {
      setSelectedProducts((prev) =>
        prev.map((p) => (p.id === productId ? { ...p, quantity: newQuantity } : p))
      );
    }
  };

  const removeProduct = (productId: number) => {
    setSelectedProducts((prev) => prev.filter((p) => p.id !== productId));
  };

  const getTotalAmount = () => {
    return selectedProducts.reduce((total, product) => total + product.price * product.quantity, 0);
  };

  const paymentMethodOptions = paymentMethods.map((method) => ({
    value: method.code,
    label: method.name,
  }));

  const adminBase = getAdminBasePath();
  const breadcrumbItems = [{ label: 'Transaction', href: `${adminBase}/transactions` }, { label: 'Add Product Sell' }];

  const handleSubmit = async (values: ProductSellFormValues, { resetForm }: any) => {
    try {
      // Validasi minimal harus ada 1 product yang dipilih
      if (selectedProducts.length === 0) {
        setNotification({
          isOpen: true,
          message: 'Minimal harus memilih 1 product untuk dijual',
          variant: 'error',
        });
        return;
      }

      // Calculate payment method fee
      const paymentMethod = paymentMethods.find((method) => method.code === values.paymentMethod);
      const baseAmount = getTotalAmount();
      const paymentFee = paymentMethod
        ? PaymentMethodApiService.calculateCustomerFee(paymentMethod, baseAmount)
        : 0;
      const totalWithFee = baseAmount + paymentFee;

      const submitData: ProductOrderPayload = {
        customerName: values.customerName,
        phoneNumber: values.phoneNumber,
        transactionDate: values.transactionDate,
        paymentMethod: values.paymentMethod,
        products: selectedProducts.map((product) => ({
          id: product.id,
          name: product.name,
          quantity: product.quantity,
          price: product.price,
          total: product.price * product.quantity,
        })),
        totalAmount: totalWithFee,
      };

      // Get arena slug
      const arenaSlug = requireArenaSlug();

      // Submit to API using the service
      const response = await productOrderService.createProductOrder(arenaSlug, submitData);

      if (response.success) {
        console.log('Success - preparing redirect');

        setNotification({
          isOpen: true,
          message: `Product order berhasil dibuat! ${response.data?.order_detail?.order_number ? `Order Number: ${response.data.order_detail.order_number}` : ''}`,
          variant: 'success',
        });

        // Reset form and selected products after successful submission
        resetForm();
        setSelectedProducts([]);

        // Check if there's an invoice URL for non-cash payment
        if (response.data?.payment_info?.invoice_url) {
          console.log('Opening invoice URL:', response.data.payment_info.invoice_url);
          // Open invoice URL in new tab
          void openExternalUrl(response.data.payment_info.invoice_url);
        }

        // Redirect after a short delay to show success message
        setTimeout(() => {
          navigate(`${adminBase}/transactions`);
        }, 1500);
      } else {
        throw new Error(response.message || 'Gagal membuat product order');
      }
    } catch (error: any) {
      console.error('Error submitting product order:', error);
      setNotification({
        isOpen: true,
        message: error.message || 'Gagal menambahkan product sell',
        variant: 'error',
      });
    }
  };

  const handleCancel = () => {
    // Navigate back to admin transactions
    navigate(`${adminBase}/transactions`);
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="hidden md:block">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Product Search */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">Add Product Sell</h3>

          {/* Search Input */}
          <div className="relative mb-4">
            <Icon
              icon="mdi:magnify"
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Product List */}
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {isLoadingProducts ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mb-2"></div>
                <p className="text-sm">Loading produk...</p>
              </div>
            ) : !Array.isArray(products) || products.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <Icon icon="mdi:package-variant-closed" className="w-16 h-16 mb-4 text-gray-300" />
                <p className="text-lg font-medium">No Data</p>
                <p className="text-sm text-center mt-2">
                  {searchQuery ? 'Tidak ditemukan produk yang sesuai' : 'Tidak ada produk tersedia'}
                </p>
              </div>
            ) : (
              products.map((product) => (
                <div
                  key={product.id}
                  className="border border-gray-200 rounded-lg p-3 flex items-center gap-3 hover:border-primary-300 transition-colors"
                >
                  <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                    <img
                      src={ProductApiService.getProductImageUrl(product)}
                      alt={product.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = '/sporto-logo.png';
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-800">{product.name}</div>
                    <div className="text-sm font-bold text-primary-600">
                      {product.price.toLocaleString('id-ID')}
                    </div>
                  </div>

                  {/* Quantity Controls or Add Button */}
                  {selectedProducts.find((p) => p.id === product.id) ? (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          const selectedProduct = selectedProducts.find((p) => p.id === product.id);
                          if (selectedProduct) {
                            updateProductQuantity(product.id, selectedProduct.quantity - 1);
                          }
                        }}
                        className="text-gray-500 border rounded-full w-6 h-6 flex items-center justify-center hover:text-gray-700"
                      >
                        <Icon icon="mdi:minus" className="w-3 h-3" />
                      </button>

                      <span className="px-2 text-sm min-w-[2rem] text-center font-medium">
                        {selectedProducts.find((p) => p.id === product.id)?.quantity || 0}
                      </span>

                      <button
                        onClick={() => {
                          const selectedProduct = selectedProducts.find((p) => p.id === product.id);
                          if (selectedProduct) {
                            updateProductQuantity(product.id, selectedProduct.quantity + 1);
                          }
                        }}
                        className="text-primary-500 border border-primary-300 rounded-full w-6 h-6 flex items-center justify-center hover:text-primary-600"
                      >
                        <Icon icon="mdi:plus" className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => addProduct(product)}
                      className="text-primary-500 border border-primary-300 rounded-full w-6 h-6 flex items-center justify-center hover:text-primary-600"
                      aria-label={`Tambah ${product.name}`}
                    >
                      <Icon icon="mdi:plus" className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Selected Products Summary */}
          {selectedProducts.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Produk Terpilih ({selectedProducts.length})
              </h4>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {selectedProducts.map((product) => (
                  <div key={product.id} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">
                      {product.name} x{product.quantity}
                    </span>
                    <span className="font-medium text-gray-800">
                      Rp {(product.price * product.quantity).toLocaleString('id-ID')}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-2 pt-2 border-t border-gray-200">
                <div className="flex items-center justify-between font-semibold">
                  <span>Total:</span>
                  <span className="text-primary-600">
                    Rp {getTotalAmount().toLocaleString('id-ID')}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Customer Form */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-6 text-gray-800">Add Product Sell</h3>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting }) => (
              <Form className="space-y-4">
                <Input
                  name="customerName"
                  label="Customer Name"
                  placeholder="Enter Name"
                  required
                />

                <Input
                  name="phoneNumber"
                  label="Phone Number"
                  placeholder="085xxx"
                  type="tel"
                  required
                />

                <Input name="transactionDate" label="Transaction Date" type="date" required />

                <Select
                  name="paymentMethod"
                  label="Payment Method"
                  options={paymentMethodOptions}
                  placeholder={
                    isLoadingPaymentMethods ? 'Loading payment methods...' : 'Select Payment Method'
                  }
                  required
                />

                {/* Action Buttons */}
                <div className="flex items-center justify-end gap-3 pt-6">
                  <Button type="button" variant="outline" onClick={handleCancel} className="px-6">
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    loading={isSubmitting}
                    className="bg-primary-500 hover:bg-primary-600 text-white px-6"
                  >
                    Add
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      {/* Notification */}
      <Notification
        isOpen={notification.isOpen}
        onClose={() => setNotification({ ...notification, isOpen: false })}
        message={notification.message}
        variant={notification.variant}
      />
    </div>
  );
};

export default ProductSell;
